import { fetchAPI, METHODS, parseBody } from './common';

/**
 * @typedef TourProgress
 * @property {boolean} completed
 * @property {string|null} completedAt
 * @property {boolean} skipped
 * @property {string|null} skippedAt
 * @property {number} stepReached
 */

/**
 * Get all tour progress for the current user/team.
 * @returns {Promise<{ progress: Object.<string, TourProgress>, settings: { areToursHidden: boolean } }>}
 */
export async function getAllProgress() {
  const res = await fetchAPI('/tour/progress', METHODS.get);
  return parseBody(res);
}

/**
 * Get tour settings for the current user/team.
 * @returns {Promise<{ areToursHidden: boolean }>}
 */
export async function getSettings() {
  const res = await fetchAPI('/tour/settings', METHODS.get);
  return parseBody(res);
}

/**
 * Get progress for a specific tour.
 * @param {string} tourKey
 * @returns {Promise<TourProgress>}
 */
export async function getProgress(tourKey) {
  const res = await fetchAPI(
    `/tour/progress/${encodeURIComponent(tourKey)}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Update progress for a specific tour.
 * @param {string} tourKey
 * @param {{ completed?: boolean, skipped?: boolean, stepReached?: number }} data
 * @returns {Promise<{ success: boolean }>}
 */
export async function updateProgress(tourKey, data) {
  const res = await fetchAPI(
    `/tour/progress/${encodeURIComponent(tourKey)}`,
    METHODS.put,
    data
  );
  return parseBody(res);
}

/**
 * Update persistent tour settings.
 * @param {{ hideAllTours?: boolean }} data
 * @returns {Promise<{ success: boolean }>}
 */
export async function updateSettings(data) {
  const res = await fetchAPI('/tour/settings', METHODS.put, data);
  return parseBody(res);
}

/**
 * Reset a specific tour.
 * @param {string} tourKey
 * @returns {Promise<{ success: boolean }>}
 */
export async function resetTour(tourKey) {
  const res = await fetchAPI(
    `/tour/progress/${encodeURIComponent(tourKey)}`,
    METHODS.delete
  );
  return parseBody(res);
}

/**
 * Reset all tours.
 * @returns {Promise<{ success: boolean }>}
 */
export async function resetAllTours() {
  const res = await fetchAPI('/tour/progress', METHODS.delete);
  return parseBody(res);
}
